import { AuthSession, type Account } from '../api/session';
import { isApiError } from '../api/client';
import { button, div, note, overlayPanel, textInput } from './dom';

// Account UI (seed, adapted per REUSE-MAP §6: the teams/jersey flows died with the football
// half). A hand-built DOM overlay over the game: Sign in → Account. All user strings render via
// textContent (T-SEC-006). The magic-link `?token` and `#join=CODE` deep-link boot logic is
// preserved: `#join=CODE` now means a ROOM join (plan §16.1) and is handed to the injected
// callback (H3 wires the real flow).

export interface AccountFlowsOpts {
  /** `#join=CODE` deep link seen at boot — join that room (H3; no-op until then is fine). */
  onJoinRoom: (code: string) => void;
  /**
   * The overlay closed (or /me answered for a stored token — boot's refresh, or a retry of one
   * that went unanswered, D302): whatever sits beneath should re-read session state. RE-READ,
   * never surface: boot's refresh settles after a `?room=` boot has already put a lobby or a
   * board up — a retry, possibly a minute into a game — and bringing the menu forward here
   * buried it (D255).
   */
  onStateChange?: () => void;
  /**
   * A magic link was just verified on a browser that was signed OUT a moment before: the one
   * moment a guest becomes an account (D256). Once per such transition, and never for
   * anything that merely re-reads a session — boot's refresh of a stored token, closing the
   * panel, or a link verified while already signed in (no guest became anybody there).
   */
  onSignedIn?: () => void;
  /**
   * Sign out was pressed, and this session's token is about to be revoked (D258). The logout
   * request waits for the returned promise — at most `SIGN_OUT_WAIT_MS` — so whatever must
   * still reach the server under the account (the telemetry queue) has LANDED while the token
   * verifies. Sending it first is not enough: a beacon issued ahead of the logout was
   * measured arriving behind it, and was filed under the browser's guest id.
   */
  onSigningOut?: () => Promise<void> | void;
  /**
   * Account deletion was confirmed (D258). Fired BEFORE the DELETE request: whatever is held on
   * the account's behalf is dropped, never sent, so nothing of the account can arrive after
   * the teardown that was meant to remove it.
   */
  onDeletingAccount?: () => void;
  /** D302: the clock the unanswered-/me retry runs on — injected so a suite can drive it. */
  setTimer?: (fn: () => void, ms: number) => number;
  clearTimer?: (h: number) => void;
  /** D302: where the browser announces `online` (default `window`). */
  connectivity?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
}

/**
 * D258: the longest Sign out waits for `onSigningOut` before revoking the token anyway. A
 * batch landing takes one round trip; a sign-out is never held hostage by telemetry.
 */
export const SIGN_OUT_WAIT_MS = 2000;

/**
 * D302: how long the flows wait before asking an unanswered /me again — then they stop until
 * the browser comes back `online` (which starts the schedule over) or the player opens the
 * Account panel (which asks at once). Six requests in ~2 minutes at most, against the server's
 * 120 a minute per IP: long enough to outlast a deploy restart and the 429 window, which is a
 * minute, and never a poll that runs for as long as the server is down.
 */
export const SESSION_RETRY_MS: readonly number[] = [2_000, 5_000, 15_000, 30_000, 60_000];

/** Run a hook and settle when its promise does or `ms` passes — whichever first; never rejects. */
function settleWithin(hook: (() => Promise<void> | void) | undefined, ms: number): Promise<void> {
  let landed: Promise<void>;
  try {
    landed = Promise.resolve(hook?.()).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    landed = Promise.resolve();
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cap = new Promise<void>((resolve) => (timer = setTimeout(resolve, ms)));
  return Promise.race([landed, cap]).finally(() => clearTimeout(timer));
}

export interface AccountFlows {
  openSignIn(): void;
  openAccount(): void;
  /** Honour a magic-link `?token` or a `#join=CODE` deep link; otherwise leave the overlay closed. */
  boot(): void;
  /** Tear down: the overlay, and the unanswered-/me retry's timer and `online` listener (D302). */
  dispose(): void;
}

export function createAccountFlows(session: AuthSession, opts: AccountFlowsOpts): AccountFlows {
  const { overlay, mount } = overlayPanel();
  overlay.style.display = 'none'; // mounts closed — the home screen beneath is the landing view
  // (D218: overlayPanel attaches itself now; this file used to be the only caller that
  // remembered to, which is exactly why the other two were silently broken.)
  const open = (): void => void (overlay.style.display = 'flex');
  const close = (): void => {
    overlay.style.display = 'none';
    opts.onStateChange?.();
  };

  const err = (e: unknown): string => (isApiError(e) ? `${e.code}: ${e.message}` : 'network error');
  const title = (): HTMLElement => {
    const h = div('display:flex;justify-content:space-between;align-items:baseline;margin-bottom:12px');
    // D239: the menu's own voice — Fredoka, cream on wood, not the FAFI display face
    h.append(div('font-family:var(--font-hud);font-weight:700;font-size:26px;letter-spacing:2px;color:var(--hud-wood-text)', 'KOLONY'));
    if (session.signedIn && session.account) h.append(div('font-size:12px;color:var(--ink-2)', session.account.displayName));
    return h;
  };

  // ── an unanswered /me (D302) ────────────────────────────────────────────────
  // refresh() keeps a stored token unless /me ANSWERS 401, so a token with no account behind
  // it is a real state now (`unconfirmed`). Whoever asks — boot, the backoff, the browser
  // coming back online, the Account panel — shares the one request in the air, and the first
  // ANSWER stops the asking and is reported once, as boot's settled refresh always was. It is
  // never a sign-in: onSignedIn belongs to verifyToken alone (D256), and a token that was
  // already here at launch and finally loads turns no guest into anybody.
  const setTimer = opts.setTimer ?? ((fn: () => void, ms: number) => globalThis.setTimeout(fn, ms) as unknown as number);
  const clearTimer = opts.clearTimer ?? ((h: number) => globalThis.clearTimeout(h));
  const connectivity = opts.connectivity ?? window;
  let asking: Promise<void> | null = null;
  let retryTimer: number | null = null;
  let retries = 0;
  let listening = false;
  let disposed = false;

  const stopAsking = (): void => {
    if (retryTimer !== null) clearTimer(retryTimer);
    retryTimer = null;
    retries = 0;
    if (listening) connectivity.removeEventListener('online', onOnline);
    listening = false;
  };
  const askAgainLater = (): void => {
    if (!listening) connectivity.addEventListener('online', onOnline);
    listening = true;
    const wait = SESSION_RETRY_MS[retries];
    if (retryTimer !== null || wait === undefined) return; // already waiting, or the schedule is spent
    retries += 1;
    retryTimer = setTimer(() => {
      retryTimer = null;
      void confirmSession();
    }, wait);
  };
  const settled = (): void => {
    asking = null;
    if (disposed) return;
    if (session.status === 'unconfirmed') return askAgainLater();
    stopAsking();
    opts.onStateChange?.(); // the account loaded, or a real 401 signed it out
  };
  /** Ask /me, or join the ask already in the air; settles once its answer — or the lack of one — is handled. */
  const confirmSession = (): Promise<void> => (asking ??= session.refresh().then(settled, settled));
  const onOnline = (): void => {
    retries = 0; // a new connection is a new chance: the schedule starts over
    if (retryTimer !== null) clearTimer(retryTimer);
    retryTimer = null;
    void confirmSession();
  };

  /**
   * Sign out, from the account view or from the unanswered one. D258: revoke only once what
   * must land under the token has landed (or the cap passed). `signOut` clears locally whatever
   * /auth/logout answers, so this is the way out even while /me never answers; it also stops
   * asking, or a pending retry would ask /me for a session that is gone.
   */
  const signOutButton = (): HTMLElement =>
    button('Sign out', () => {
      void settleWithin(opts.onSigningOut, SIGN_OUT_WAIT_MS)
        .then(() => session.signOut())
        .then(() => {
          stopAsking();
          close();
        });
    });

  // ── SIGN IN ──────────────────────────────────────────────────────────────────
  const signIn = (): void => {
    const c = div();
    c.append(title());
    c.append(div('font-size:21px;font-weight:700;margin:8px 0', 'Sign in'));
    c.append(div('font-size:13px;color:var(--ink-2);margin-bottom:6px', 'Optional — sign in to be ranked. Guests always play free.'));
    const email = textInput('you@example.com', '', 'email');
    const msg = note('');
    const send = button('Send magic link', () => {
      msg.textContent = 'sending…';
      session.api
        .post('/auth/magic-link', { email: email.value.trim() })
        .then(() => (msg.textContent = '✓ Check your email for the sign-in link (in dev, see the server console).'))
        .catch((e) => (msg.textContent = err(e)));
    }, { primary: true });
    c.append(email, send, msg);
    // dev/paste fallback: accept a magic-link token directly
    c.append(div('margin-top:14px;color:var(--ink-3);font-size:12px', 'Have a sign-in link/token? Paste it:'));
    const tok = textInput('token from the link');
    c.append(tok, button('Verify token', () => verifyToken(tok.value.trim())));
    c.append(div('margin-top:14px'), button('← Back', () => close()));
    mount(c);
  };

  const verifyToken = (token: string): void => {
    if (!token) return;
    // D256: read before the exchange. On the `?token` boot this runs after the stored
    // session's refresh has settled, so a token the server just refused already reads as
    // signed out — a lapsed session followed by a fresh link is a real sign-in. One that went
    // UNANSWERED keeps its token (D302), so that link counts as a re-verify: unanswered, a
    // stale token and a live one look alike, and a sign-in that was not one would link this
    // browser's guest trail to a second account.
    const wasSignedIn = session.signedIn;
    session
      .verify(token)
      .then(() => {
        if (!wasSignedIn) opts.onSignedIn?.();
        account();
      })
      .catch((e) => {
        const c = div();
        c.append(title(), note(`Sign-in failed — ${err(e)}`, 'var(--danger-ink)'), button('← Back', () => signIn()));
        mount(c);
      });
  };

  // ── ACCOUNT ──────────────────────────────────────────────────────────────────
  const account = (): void => {
    const a = session.account;
    if (!a) return void close();
    const c = div();
    c.append(title());
    c.append(div('font-size:21px;font-weight:700;margin:8px 0', 'Account'), div('color:var(--hud-ink-muted);font-size:13.5px', a.email));
    c.append(div('margin-top:10px', 'Display name'));
    const name = textInput('display name', a.displayName);
    const msg = note('');
    c.append(
      name,
      button('Save name', () => {
        session.api
          .patch<{ account: Account }>('/me', { displayName: name.value.trim() })
          .then((r) => ((session.account = r.account), (msg.textContent = '✓ saved')))
          .catch((e) => (msg.textContent = err(e)));
      }),
      msg,
    );
    c.append(div('margin-top:16px'), signOutButton());
    c.append(div('margin-top:16px'));
    c.append(button('Delete account', () => confirmDelete()));
    c.append(div('margin-top:12px'), button('← Back', () => close()));
    mount(c);
  };

  /**
   * The Account button. A token whose /me went unanswered (D302) still reads as signed in —
   * so the home screen offers "Account" — but has no account to draw, and account() closes on
   * that: the button looked dead. Ask /me first, and say so when the server still cannot answer.
   */
  const openAccount = (): void => {
    open();
    if (session.status !== 'unconfirmed') return account(); // loaded — or signed out, which account() closes on
    const c = div();
    // a way out while asking: a fetch has no timeout of its own, and a hung one must not hold the panel
    c.append(title(), note('loading your account…'), div('margin-top:12px'), button('← Back', () => close()));
    mount(c);
    void confirmSession().then(() => {
      if (session.status === 'signed-in') return account();
      if (session.status === 'signed-out') return signIn(); // a 401: the stored sign-in had lapsed
      const u = div();
      u.append(
        title(),
        div('font-size:21px;font-weight:700;margin:8px 0', 'Account'),
        note('The server is not answering right now. Your sign-in is kept — try again in a moment.'),
        button('Try again', () => openAccount(), { primary: true }),
        // the one way out when /me never answers at all: signing out needs no answer from it
        div('margin-top:12px'),
        signOutButton(),
        div('margin-top:12px'),
        button('← Back', () => close()),
      );
      mount(u);
    });
  };

  const confirmDelete = (): void => {
    const c = div();
    c.append(title(), note('Delete your account? Your games remain recorded without your name. This cannot be undone.', 'var(--danger-ink)'));
    c.append(
      button('Yes, delete', () => {
        opts.onDeletingAccount?.(); // D258: before the request, so nothing held for the account outlives it
        void session.api
          .delete('/me')
          .then(() => (session.setToken(null), close()))
          .catch(() => close());
      }),
      button('Cancel', () => account()),
    );
    mount(c);
  };

  // ── boot: honour a magic-link ?token or a #join=CODE deep link (else stay closed) ──
  const boot = (): void => {
    const url = new URL(location.href);
    const magic = url.searchParams.get('token');
    const joinHash = location.hash.startsWith('#join=') ? location.hash.slice(6) : null;
    if (magic) {
      url.searchParams.delete('token');
      history.replaceState(null, '', url); // strip the token from the address bar
      open();
      const c = div();
      c.append(title(), note('verifying sign-in…'));
      mount(c);
      session.refresh().finally(() => verifyToken(magic));
    } else if (joinHash) {
      opts.onJoinRoom(joinHash); // room deep link (plan §16.1); session refresh rides along below
      if (session.signedIn) void confirmSession();
    } else if (session.signedIn) {
      // the state change fires once /me ANSWERS — it loaded the account, or a 401 cleared it;
      // unanswered, it is asked again on the backoff and when the browser comes back online
      void confirmSession();
    }
    // signed out with no params: nothing to do — the home screen is already the landing view
  };

  return {
    openSignIn: () => (open(), signIn()),
    openAccount,
    boot,
    dispose: () => {
      disposed = true;
      stopAsking();
      overlay.remove();
    },
  };
}
